import { IMAGE_PREFIX } from './constants';

/**
 * Utilities to support imgproxy-served images (imgix has been fully retired).
 *
 * Per Life.Church's imgproxy design, the API resolves dynamic image sources
 * into a complete imgproxy URL server-side before returning them — this app
 * never receives a raw, unresolved dynamic source (e.g. a gs:// URI).
 * ChopImage only ever needs to detect an existing imgproxy URL and splice
 * in processing options; it never builds one from scratch for dynamic
 * content. Anything that's not an imgproxy URL (e.g. a third-party OAuth
 * avatar) is left untouched — imgproxy isn't allowlisted to fetch arbitrary
 * external hosts.
 *
 * Static assets are the one exception: there's no API call resolving them,
 * so `buildStaticImageUrl` builds the imgproxy URL from scratch client-side.
 *
 * imgproxy URL anatomy: requests aren't signed — the path uses a literal
 * `_` in place of a signature — and the source is base64url-encoded (no
 * padding), chunked into 16-character segments:
 *
 *   {base}/_/{option}:{value}/.../{base64url_source_chunked_every_16_chars}
 */

export interface ImgproxyOptions {
  dpr?: number;
  /** Where to crop from when `resizingType: 'fill'` shrinks a non-matching
   * aspect ratio down to the target box — 'sm' asks imgproxy for a smart
   * crop (its closest equivalent to imgix's `crop: 'faces,entropy'`).
   * Silently ignored by imgproxy if unsupported by the deployed config. */
  gravity?: 'ce' | 'no' | 'so' | 'ea' | 'we' | 'sm';
  height?: number;
  quality?: number;
  resizingType?: 'auto' | 'fill' | 'fit' | 'force';
  width?: number;
}

export const isImgproxyUrl = (src: string): boolean => {
  try {
    return new URL(src).hostname === new URL(IMAGE_PREFIX).hostname;
  } catch {
    return false;
  }
};

/**
 * Splices processing options into an existing imgproxy URL, right after
 * the `_` placeholder and before the (untouched) base64url-encoded source.
 * The API always returns option-less URLs, so there's no need to detect or
 * replace any pre-existing options.
 */
export const insertImgproxyOptions = (
  imgproxyUrl: string,
  options: ImgproxyOptions = {}
): string => {
  const optionSegments: string[] = [];
  if (options.width || options.height) {
    optionSegments.push(
      `rs:${options.resizingType ?? 'fit'}:${options.width ?? 0}:${options.height ?? 0}`
    );
  }
  if (options.gravity) {
    optionSegments.push(`g:${options.gravity}`);
  }
  if (options.quality) {
    optionSegments.push(`q:${options.quality}`);
  }
  if (options.dpr && options.dpr !== 1) {
    optionSegments.push(`dpr:${options.dpr}`);
  }

  if (optionSegments.length === 0) {
    return imgproxyUrl;
  }

  let url: URL;
  try {
    url = new URL(imgproxyUrl);
  } catch {
    return imgproxyUrl;
  }

  const [signature, ...rest] = url.pathname.replace(/^\//, '').split('/');
  url.pathname = `/${[signature, ...optionSegments, ...rest].join('/')}`;
  return url.toString();
};

/**
 * Resolves a source to a processed imgproxy URL for consumers that don't go
 * through ChopImage (e.g. a plain <img>/background-image, or a third-party
 * component that only accepts a finished `src`). Non-imgproxy sources (e.g.
 * a third-party OAuth avatar) pass through untouched, same as ChopImage's
 * own fallback behavior.
 */
export const resolveImageUrl = (
  src: string | null | undefined,
  options: ImgproxyOptions
): string => {
  if (!src) {
    return '';
  }
  return isImgproxyUrl(src) ? insertImgproxyOptions(src, options) : src;
};

/**
 * Resolves an avatar source to a `size`x`size` imgproxy URL, cropped to fill
 * (matching the `object-cover` styling every avatar consumer applies), with
 * a smart-crop gravity so the crop favors faces over a naive center crop.
 */
export const resolveAvatarSrc = (src: string | null | undefined, size: number): string =>
  resolveImageUrl(src, { gravity: 'sm', height: size, resizingType: 'fill', width: size });

const STATIC_ASSET_BUCKET = 'gs://chop-profile';

const BASE64URL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

// Dependency-free base64url (no padding) encoder — `Buffer` isn't polyfilled
// for this project's client bundle, and `TextEncoder` is available in both
// the browser and Node, so this works isomorphically.
const toBase64Url = (input: string): string => {
  const bytes = new TextEncoder().encode(input);
  let result = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    result += BASE64URL_CHARS[b0 >> 2];
    result += BASE64URL_CHARS[((b0 & 0x03) << 4) | (b1 === undefined ? 0 : b1 >> 4)];
    if (b1 !== undefined) {
      result += BASE64URL_CHARS[((b1 & 0x0f) << 2) | (b2 === undefined ? 0 : b2 >> 6)];
    }
    if (b2 !== undefined) {
      result += BASE64URL_CHARS[b2 & 0x3f];
    }
  }
  return result;
};

/**
 * Builds an imgproxy URL for a static asset from scratch — there's no API
 * resolving these into ready-made imgproxy URLs the way dynamic content is,
 * so this constructs the `_`-placeholder + base64url-encoded-source anatomy
 * directly. `path` is the same relative path already used against the old
 * imgix static prefix (e.g. `static/admin/first_steps_check.png`); the GCS
 * bucket housing these assets is fixed (`gs://chop-profile`).
 */
export const buildStaticImageUrl = (path: string): string => {
  const gcsUri = `${STATIC_ASSET_BUCKET}/${path}`;
  const encoded = toBase64Url(gcsUri);
  const chunked = encoded.match(/.{1,16}/g)?.join('/') ?? encoded;
  return `${IMAGE_PREFIX}/_/${chunked}`;
};
